/**
 * Small-cell privacy for client self-serve (M4S-5777 X135, round-2 decision SV4).
 * Spec: docs/pdlc/crosstabs-saving-and-access.md §8.
 *
 * An account's minimum group size (the floor, set by MFour staff on /tools/crosstab-privacy) hides
 * every figure about fewer people than that from the account's users, with complementary hiding so
 * a hidden figure cannot be worked out from the ones left. A crosstab's author can raise the floor
 * for that crosstab, never lower it (`effectivePrivacyFloor`). This is about privacy, not reliability:
 * the low-base greying (X76) is separate and unchanged.
 *
 * What is hidden, given a floor F (a group of n people is "small" when 0 < n < F):
 *
 * 1. **Columns.** A banner column whose base is small is dropped from the table, every block, every
 *    export. When the Total itself is small, every figure is hidden.
 * 2. **Complementary columns.** In a banner group whose columns do not overlap, a single dropped
 *    column could be read as Total minus the others, so the next smallest column of that group is
 *    dropped too.
 * 3. **A question's answerer base.** A column of a block whose own answerer base is small is blanked
 *    for that block, base row included.
 * 4. **Cells.** An answer or net cell counting a small number of people is blanked.
 * 5. **Complementary cells.** A blank that stands alone down a column of a block (base minus the
 *    rest) or across a row within a non-overlapping banner group (Total minus the rest) takes the
 *    next smallest non-zero cell with it. Repeated until nothing changes.
 *
 * When the Total itself is small, or the floor cannot be read, no table is shown at all, only the
 * note. A blanked cell keeps no figure: no percent, count, letters, arrow, mean or score. Every renderer
 * already prints a dash for a cell without a figure, so the table, Excel, PowerPoint, PDF, SPSS,
 * the chart, Ask DANI's context and the iOOH deck all read the same hidden table: the workspace
 * hides at its one `computed` table, and anything built from it inherits the hiding.
 *
 * NOT A SECURITY BOUNDARY. Decision 1 keeps the computing in the browser, which holds every
 * respondent's answers, so a determined user can read what this hides. It keeps small groups off the
 * screen and out of every file; enforcing it needs the compute on the server (spec §8, "Limits").
 *
 * Pure: no React, no requests.
 */

import type {
    Block,
    Cell,
    ComputedTable,
    TableRow,
} from '@/app/(private)/ai/components/Crosstabs/data';

/** The smallest and largest floor anyone may set. 1 would hide nothing. */
export const PRIVACY_FLOOR_MIN = 2;
export const PRIVACY_FLOOR_MAX = 1000;

/** A floor from an untrusted value: a whole number in range, or null for "none". */
export function parsePrivacyFloor(raw: unknown): number | null {
    const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
    if (typeof n !== 'number' || !Number.isInteger(n)) return null;
    if (n < PRIVACY_FLOOR_MIN || n > PRIVACY_FLOOR_MAX) return null;
    return n;
}

/**
 * The floor a table is shown under: the account's, raised (never lowered) by the crosstab's own.
 * 0 means no floor.
 */
export function effectivePrivacyFloor(
    accountFloor: number | null | undefined,
    crosstabFloor: number | null | undefined,
): number {
    return Math.max(parsePrivacyFloor(accountFloor) ?? 0, parsePrivacyFloor(crosstabFloor) ?? 0);
}

/** What the hiding did, carried on the table so the notes and the workspace can say so. */
export interface PrivacyApplied {
    floor: number;
    /** Columns dropped, by id. */
    hiddenColumns: string[];
    /** Cells blanked in the columns that remain. */
    hiddenCells: number;
    /** The Total itself is a small group: nothing is shown. */
    allHidden: boolean;
    /** The account's floor could not be read, so nothing is shown (`withholdFigures`). */
    unread?: boolean;
    /** The account's floor is still being read; nothing is shown yet. */
    checking?: boolean;
}

/** The line every export prints under a table that hid something. */
export function privacyNoteLine(p: PrivacyApplied): string {
    if (p.checking) return "Checking this account's minimum group size…";
    if (p.unread) {
        return "Not shown: this account's minimum group size could not be checked. Reload to try again.";
    }
    if (p.allHidden) {
        return `Not shown: fewer than ${p.floor} people in this table (minimum group size for privacy).`;
    }
    return `Figures for groups of fewer than ${p.floor} people are hidden, with the figures they could be worked out from (minimum group size for privacy).`;
}

const isSmall = (n: number | null | undefined, floor: number) =>
    typeof n === 'number' && n > 0 && n < floor;

function blank(cell: Cell): Cell {
    return {
        ...cell,
        value: 0,
        pct: null,
        count: null,
        letters: [],
        letters2: [],
        vsTotal: null,
        low: true,
        suppressed: true,
        score: undefined,
        statValue: null,
        sd: undefined,
    };
}

const isCounted = (row: TableRow) => row.kind === 'option' || row.kind === 'net';

/** Drops columns from the table's every part. */
function dropColumns(table: ComputedTable, drop: ReadonlySet<string>): ComputedTable {
    if (drop.size === 0) return table;
    const base: Record<string, number> = {};
    for (const [id, n] of Object.entries(table.base)) if (!drop.has(id)) base[id] = n;
    return {
        ...table,
        cols: table.cols.filter((c) => !drop.has(c.id)),
        base,
        banner: table.banner
            .map((g) => ({ ...g, cols: g.cols.filter((c) => !drop.has(c.id)) }))
            .filter((g) => g.cols.length > 0),
        blocks: table.blocks.map((b) => ({
            ...b,
            ...(b.blockBase
                ? {
                      blockBase: Object.fromEntries(
                          Object.entries(b.blockBase).filter(([id]) => !drop.has(id)),
                      ),
                  }
                : {}),
            rows: b.rows.map((r) => ({ ...r, cells: r.cells.filter((c) => !drop.has(c.col)) })),
        })),
    };
}

/** The columns to drop: small ones, plus one complement in each non-overlapping group. */
function columnsToDrop(table: ComputedTable, floor: number): Set<string> {
    const drop = new Set<string>();
    for (const col of table.cols) {
        if (col.id === 'total') continue;
        if (isSmall(table.base[col.id], floor)) drop.add(col.id);
    }
    for (const group of table.banner) {
        if (group.overlap) continue;
        const ids = group.cols.map((c) => c.id).filter((id) => id !== 'total');
        const dropped = ids.filter((id) => drop.has(id));
        if (dropped.length !== 1) continue;
        const next = ids
            .filter((id) => !drop.has(id) && (table.base[id] ?? 0) > 0)
            .sort((a, b) => (table.base[a] ?? 0) - (table.base[b] ?? 0))[0];
        if (next) drop.add(next);
    }
    return drop;
}

/** Blanks small cells and their complements in one block. Returns the block and the count. */
function hideCells(
    block: Block,
    floor: number,
    groups: ReadonlyArray<ReadonlyArray<string>>,
): { block: Block; hidden: number } {
    const rows = block.rows.map((r) => ({ ...r, cells: [...r.cells] }));
    const hidden = new Set<string>();
    const keyOf = (ri: number, ci: number) => `${ri}:${ci}`;
    const hide = (ri: number, ci: number) => {
        hidden.add(keyOf(ri, ci));
        rows[ri].cells[ci] = blank(rows[ri].cells[ci]);
    };

    // 3. A column whose answerer base for this block is small: blank the whole column of it.
    if (block.blockBase) {
        rows.forEach((row, ri) =>
            row.cells.forEach((cell, ci) => {
                if (isSmall(block.blockBase?.[cell.col], floor)) hide(ri, ci);
            }),
        );
    }

    // 4. Small counted cells.
    rows.forEach((row, ri) => {
        if (!isCounted(row)) return;
        row.cells.forEach((cell, ci) => {
            if (!hidden.has(keyOf(ri, ci)) && isSmall(cell.count, floor)) hide(ri, ci);
        });
    });

    // 5. Complements, until nothing changes. Each pass hides at least one more cell or stops.
    const colIndex = new Map<string, number>();
    rows[0]?.cells.forEach((c, ci) => colIndex.set(c.col, ci));
    const smallestOther = (candidates: Array<[number, number]>): [number, number] | null => {
        const open = candidates.filter(
            ([ri, ci]) => !hidden.has(keyOf(ri, ci)) && (rows[ri].cells[ci].count ?? 0) > 0,
        );
        open.sort(
            ([ar, ac], [br, bc]) =>
                (rows[ar].cells[ac].count ?? 0) - (rows[br].cells[bc].count ?? 0),
        );
        return open[0] ?? null;
    };
    const optionRows = rows.map((r, ri) => (r.kind === 'option' ? ri : -1)).filter((i) => i >= 0);
    let changed = true;
    let guard = 0;
    while (changed && guard < 10_000) {
        changed = false;
        guard += 1;
        // Down a column: option rows of a question, base minus the rest.
        const width = rows[0]?.cells.length ?? 0;
        for (let ci = 0; ci < width; ci++) {
            const inCol = optionRows.filter((ri) => hidden.has(keyOf(ri, ci)));
            if (inCol.length !== 1) continue;
            const next = smallestOther(optionRows.map((ri) => [ri, ci] as [number, number]));
            if (next) {
                hide(next[0], next[1]);
                changed = true;
            }
        }
        // Across a row, within a non-overlapping banner group: Total minus the rest.
        rows.forEach((row, ri) => {
            if (!isCounted(row)) return;
            for (const group of groups) {
                const cis = group
                    .map((id) => colIndex.get(id))
                    .filter((ci): ci is number => ci != null);
                const blanks = cis.filter((ci) => hidden.has(keyOf(ri, ci)));
                if (blanks.length !== 1) continue;
                const next = smallestOther(cis.map((ci) => [ri, ci] as [number, number]));
                if (next) {
                    hide(next[0], next[1]);
                    changed = true;
                }
            }
        });
    }

    return { block: { ...block, rows }, hidden: hidden.size };
}

/**
 * No figure at all: no table, column, base or n. What is shown when the Total itself is a small
 * group, or while the account's floor cannot be read (`unread`). The note says which.
 */
function nothingShown(table: ComputedTable, privacy: PrivacyApplied): ComputedTable {
    return {
        ...table,
        banner: [],
        cols: [],
        base: {},
        blocks: [],
        totalN: 0,
        totalEff: 0,
        privacy,
    };
}

/**
 * The table as shown while an account's floor cannot be read, or is still being read (`checking`):
 * nothing, and a note saying why.
 */
export function withholdFigures(table: ComputedTable, checking = false): ComputedTable {
    return nothingShown(table, {
        floor: 0,
        hiddenColumns: [],
        hiddenCells: 0,
        allHidden: true,
        unread: true,
        ...(checking ? { checking: true } : {}),
    });
}

/**
 * The table as an account under `floor` may see it. A floor of 0 or 1 returns the table unchanged.
 */
export function applyPrivacyFloor(table: ComputedTable, floor: number): ComputedTable {
    if (!Number.isFinite(floor) || floor < PRIVACY_FLOOR_MIN) return table;

    if (isSmall(table.base.total ?? table.totalN, floor)) {
        return nothingShown(table, { floor, hiddenColumns: [], hiddenCells: 0, allHidden: true });
    }

    const drop = columnsToDrop(table, floor);
    const kept = dropColumns(table, drop);
    const groups = kept.banner
        .filter((g) => !g.overlap)
        .map((g) => g.cols.map((c) => c.id).filter((id) => id !== 'total'));
    let hiddenCells = 0;
    const blocks = kept.blocks.map((b) => {
        const { block, hidden } = hideCells(b, floor, groups);
        hiddenCells += hidden;
        return block;
    });
    if (drop.size === 0 && hiddenCells === 0) return table;
    return {
        ...kept,
        blocks,
        privacy: { floor, hiddenColumns: [...drop], hiddenCells, allHidden: false },
    };
}
